import React from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { endOfDay, startOfDay } from "date-fns";
import { Loader2, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PresignedImageWithPlaceholder } from "@/components/common/PresignedImageWithPlaceholder";
import { cn } from "@/lib/utils";
import { ExportFilterPopover } from "./components/ExportFilterPopover";
import { RevenueLogsPanel } from "./components/RevenueLogsPanel";
import { partnerDashboardService } from "@/services/partner-dashboard-service";
import { PartnerRevenueLogRecord } from "@/types/partner-dashboard";
import { LocalStorageKeys } from "../login/hooks/useLocalStorage";
import { useAccessCapabilities } from "@/hooks/useAccessCapabilities";
import viatorLogo from "@/assets/svg/viator-logo.svg";
import {
  GuesteriaPartnerRevenueRow,
  mapOverviewToRow,
} from "./guesteriaPartnerRevenueModel";

type LocationState = {
  row?: GuesteriaPartnerRevenueRow;
};

function getCurrentMonthDateRange(): { from: string; to: string } {
  const now = new Date();
  return {
    from: startOfDay(
      new Date(now.getFullYear(), now.getMonth(), 1),
    ).toISOString(),
    to: endOfDay(now).toISOString(),
  };
}

export const GuesteriaPartnersRevenueDetailPage = (): React.JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { capabilities } = useAccessCapabilities();
  const canExport = capabilities.canManageBilling;
  const { hotelId } = useParams<{ hotelId: string }>();
  const { state } = useLocation() as { state: LocationState | null };
  const rowFromNavigation =
    state?.row && hotelId && state.row.id === hotelId ? state.row : undefined;

  const [row, setRow] = React.useState<GuesteriaPartnerRevenueRow | undefined>(
    rowFromNavigation,
  );
  const [rowLoading, setRowLoading] = React.useState(!rowFromNavigation);

  const [selectedExportFormat, setSelectedExportFormat] = React.useState<
    "CSV" | "Excel" | null
  >("CSV");
  const [logs, setLogs] = React.useState<PartnerRevenueLogRecord[]>([]);
  const [logsLoading, setLogsLoading] = React.useState(false);

  React.useEffect(() => {
    if (rowFromNavigation) {
      setRow(rowFromNavigation);
      setRowLoading(false);
      return;
    }

    if (!hotelId) {
      setRow(undefined);
      setRowLoading(false);
      return;
    }

    const tenantId = localStorage.getItem(LocalStorageKeys.TENANT_ID);
    if (!tenantId) {
      setRow(undefined);
      setRowLoading(false);
      return;
    }

    let cancelled = false;
    setRow(undefined);
    setRowLoading(true);

    const loadRow = async () => {
      try {
        const { from, to } = getCurrentMonthDateRange();
        const data = await partnerDashboardService.getHotels(tenantId, {
          from,
          to,
          hotelIds: [hotelId],
        });
        if (!cancelled) {
          const overview = data[0];
          setRow(overview ? mapOverviewToRow(overview) : undefined);
        }
      } catch {
        if (!cancelled) {
          setRow(undefined);
        }
      } finally {
        if (!cancelled) {
          setRowLoading(false);
        }
      }
    };

    void loadRow();
    return () => {
      cancelled = true;
    };
  }, [hotelId, rowFromNavigation]);

  React.useEffect(() => {
    const tenantId = localStorage.getItem(LocalStorageKeys.TENANT_ID);
    if (!tenantId || !hotelId) {
      setLogs([]);
      return;
    }

    let isMounted = true;
    const loadRevenueLogs = async () => {
      try {
        setLogsLoading(true);
        const { from, to } = getCurrentMonthDateRange();
        const data = await partnerDashboardService.getRevenueLogs(tenantId, {
          from,
          to,
          hotelId,
          page: 1,
          limit: 20,
        });
        if (isMounted) {
          setLogs(data.data);
        }
      } catch {
        if (isMounted) {
          setLogs([]);
        }
      } finally {
        if (isMounted) {
          setLogsLoading(false);
        }
      }
    };

    void loadRevenueLogs();
    return () => {
      isMounted = false;
    };
  }, [hotelId]);

  const handleExport = React.useCallback(
    async (nextFormat: "CSV" | "Excel" | null) => {
      if (!canExport) return;

      setSelectedExportFormat(nextFormat);
      if (!nextFormat || !hotelId) return;

      const tenantId = localStorage.getItem(LocalStorageKeys.TENANT_ID);
      if (!tenantId) return;

      const { from, to } = getCurrentMonthDateRange();
      const format = nextFormat === "CSV" ? "csv" : "xlsx";
      const blob = await partnerDashboardService.exportRevenueLogs(tenantId, {
        from,
        to,
        hotelId,
        format,
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `partner-revenue-logs-${hotelId}.${format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },
    [canExport, hotelId],
  );

  if (rowLoading) {
    return (
      <div className="p-6">
        <Button
          type="button"
          variant="ghost"
          className="mb-6 h-8 rounded-md bg-transparent px-0 py-1 text-base font-normal text-black hover:bg-transparent"
          onClick={() => navigate("/guesteria-partners-revenue")}
        >
          <Undo2 className="size-5" />
          {t("billing.guesteriaPartnersRevenue.details.back")}
        </Button>
        <div className="flex flex-col items-center justify-center gap-3 py-12">
          <Loader2 className="size-8 animate-spin text-zinc-400" aria-hidden />
          <p className="text-sm text-zinc-500">
            {t("billing.guesteriaPartnersRevenue.details.loading")}
          </p>
        </div>
      </div>
    );
  }

  if (!row) {
    return (
      <div className="p-6">
        <Button
          type="button"
          variant="ghost"
          className="mb-6 h-8 rounded-md bg-transparent px-0 py-1 text-base font-normal text-black hover:bg-transparent"
          onClick={() => navigate("/guesteria-partners-revenue")}
        >
          <Undo2 className="size-5" />
          {t("billing.guesteriaPartnersRevenue.details.back")}
        </Button>
        <p className="text-sm text-zinc-600">
          {t("billing.guesteriaPartnersRevenue.details.notFound")}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col p-6">
      <div className="mb-6 flex items-center justify-between">
        <Button
          type="button"
          variant="ghost"
          className="h-8 rounded-md bg-transparent px-0 py-1 text-base font-normal text-black hover:bg-transparent"
          onClick={() => navigate("/guesteria-partners-revenue")}
        >
          <Undo2 className="size-5" />
          {t("billing.guesteriaPartnersRevenue.details.back")}
        </Button>
        {canExport ? (
          <ExportFilterPopover
            selectedFormat={selectedExportFormat}
            onApply={(nextFormat) => {
              void handleExport(nextFormat);
            }}
          />
        ) : null}
      </div>

      <div className="mx-auto mb-6 flex max-w-xl flex-col items-center gap-3 py-2">
        <PresignedImageWithPlaceholder
          className="h-32 w-32 rounded-2xl object-cover shadow-sm"
          photo={row.hotelLogoId}
          alt={row.hotelBrand}
          placeholder={
            <div
              className={cn(
                "flex h-32 w-32 items-center justify-center rounded-2xl font-serif text-5xl font-semibold text-amber-300 shadow-sm",
                row.hotelLogoClassName,
              )}
            >
              {row.hotelLogoLetter}
            </div>
          }
        />
        <h2 className="text-center text-lg leading-none font-medium text-zinc-950">
          {row.hotelBrand}
        </h2>
      </div>

      <div className="mb-4 rounded-xl bg-white px-6 pt-6 pb-0">
        <div className="grid grid-cols-[140px_1fr] gap-x-6 gap-y-4">
          <span className="flex min-h-8 items-center text-sm leading-normal font-medium text-zinc-950">
            {t("billing.guesteriaPartnersRevenue.admin")}:
          </span>
          <span className="flex min-h-8 items-center gap-3">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-zinc-200 text-sm font-medium text-zinc-700">
              {row.adminInitials}
            </span>
            <span className="text-sm leading-normal font-medium text-zinc-950">
              {row.adminName}
            </span>
          </span>

          <span className="flex min-h-8 items-center text-sm leading-normal font-medium text-zinc-950">
            {t("billing.guesteriaPartnersRevenue.partner")}:
          </span>
          <span className="flex min-h-8 w-fit flex-col items-center gap-1">
            <img
              src={viatorLogo}
              alt="Viator"
              className="h-12 w-12 rounded-xl object-contain"
            />
            <span className="inline-flex h-[12px] w-[40px] items-center justify-center text-center text-[10px] leading-[100%] font-normal text-[#0A0A0A]">
              {row.partnerName}
            </span>
          </span>

          <span className="flex min-h-8 items-center text-sm leading-normal font-medium text-zinc-950">
            {t("billing.guesteriaPartnersRevenue.details.currency")}:
          </span>
          <span className="flex min-h-8 items-center text-sm leading-normal text-zinc-500">
            {t("billing.guesteriaPartnersRevenue.details.currencyEuro")}
          </span>

          <span className="flex min-h-8 items-center text-sm leading-normal font-medium text-zinc-950">
            {t("billing.guesteriaPartnersRevenue.revenue")}:
          </span>
          <span className="flex min-h-8 items-center text-sm leading-normal text-zinc-500">
            €{row.revenueEur.toLocaleString("en-GB")}
          </span>

          <span className="flex min-h-8 items-center text-sm leading-normal font-medium text-zinc-950">
            {t("billing.guesteriaPartnersRevenue.pms")}:
          </span>
          <span className="flex min-h-8 items-center text-sm leading-normal text-zinc-500">
            {row.pms}
          </span>

          <span className="flex min-h-8 items-center text-sm leading-normal font-medium text-zinc-950">
            {t("billing.guesteriaPartnersRevenue.billingPlan")}:
          </span>
          <span className="flex min-h-8 items-center text-sm leading-normal text-zinc-500">
            {row.billingPlan}
          </span>
        </div>
      </div>

      <RevenueLogsPanel logs={logs} loading={logsLoading} />
    </div>
  );
};
